import { NavLink, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { 
  Home,
  FolderKanban,
  Heart,
  Compass,
  Settings,
  LogOut,
  Menu,
  X,
  BookOpen,
  ChevronDown,
} from "lucide-react";
import { useState, useMemo } from "react";
import { getRandomTip } from "@/data/dailyTips";

// Main 4 navigation areas (kept for the mobile bottom nav only — desktop top nav was removed)
const MAIN_NAV_ITEMS = [
  { path: "/oggi", label: "Oggi", icon: Home },
  { path: "/organizza", label: "Organizza", icon: FolderKanban },
  { path: "/benessere", label: "Benessere", icon: Heart },
  { path: "/percorsi", label: "Percorsi", icon: Compass },
];

// =================== HEADER TIP BANNER (silver bg, rotates each app entry) ===================
// Rendered only on / and /oggi to keep internal pages uncluttered. The tip itself
// uses useMemo with no deps so it stays stable for the session; a different one
// will appear on the next reload.
const HeaderTipBanner = () => {
  const [showRef, setShowRef] = useState(false);
  const tip = useMemo(() => getRandomTip(), []);
  return (
    <div
      className="bg-gradient-to-b from-slate-100 to-slate-50 border-b border-slate-200"
      data-testid="header-tip-banner"
    >
      <div className="container mx-auto max-w-7xl px-4 py-2.5 sm:py-3">
        <p className="text-xs sm:text-sm leading-relaxed text-slate-600 font-light italic">
          {tip.text}
        </p>
        <button
          onClick={() => setShowRef(!showRef)}
          className="flex items-center gap-1 mt-1 text-[10px] sm:text-[11px] text-slate-500 hover:text-slate-700 transition-colors font-light tracking-wide"
          data-testid="header-tip-reference-toggle"
        >
          <BookOpen className="w-3 h-3" strokeWidth={1.5} />
          Riferimenti scientifici
          <ChevronDown className={`w-3 h-3 transition-transform ${showRef ? "rotate-180" : ""}`} strokeWidth={1.5} />
        </button>
        {showRef && (
          <p className="text-[10px] sm:text-[11px] text-slate-500 leading-relaxed italic font-light mt-1.5 pt-1.5 border-t border-slate-200" data-testid="header-tip-reference-content">
            {tip.reference}
          </p>
        )}
      </div>
    </div>
  );
};

const Navigation = ({ user, onLogout }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/" || location.pathname === "/oggi";

  return (
    <>
      {/* Top Header Bar */}
      <header className="border-b border-slate-200 bg-background/95 backdrop-blur-md sticky top-0 z-50 safe-area-top" data-testid="navigation">
        <div className="container mx-auto max-w-7xl px-4">
          {/* Row 1: Hello Revela + subtitle + user actions */}
          <div className="flex items-center justify-between gap-4 h-16 sm:h-20">
            {/* Brand: "Hello" small + "Revela" large */}
            <NavLink to="/oggi" className="flex items-baseline gap-1.5 sm:gap-2 shrink-0" data-testid="brand-link">
              <span className="font-display text-base sm:text-lg text-slate-500 font-light italic" data-testid="brand-hello">
                Hello
              </span>
              <h1 className="font-display text-3xl sm:text-4xl tracking-tight text-silver leading-none" data-testid="brand-revela">
                Revela
              </h1>
            </NavLink>

            {/* Subtitle — hidden on small screens to keep header clean */}
            <p
              className="hidden md:block font-display text-sm lg:text-base text-slate-500 italic font-light leading-snug text-center flex-1 min-w-0 px-3"
              data-testid="brand-subtitle"
            >
              Traccia le tue giornate e scopri i tuoi pattern.
            </p>

            {/* Desktop Navigation REMOVED (27/05/2026) — main hubs live under "Altre funzioni" on Home */}

            {/* User Menu - Desktop */}
            <div className="hidden md:flex items-center gap-3">
              {user && (
                <>
                  <NavLink
                    to="/impostazioni"
                    className={({ isActive }) =>
                      `p-2 rounded-full transition-colors ${
                        isActive ? "bg-muted" : "hover:bg-muted"
                      }`
                    }
                  >
                    <Settings className="w-5 h-5 text-muted-foreground" />
                  </NavLink>
                  <div className="flex items-center gap-2">
                    {user.picture && (
                      <img 
                        src={user.picture} 
                        alt={user.name} 
                        className="w-8 h-8 rounded-full"
                      />
                    )}
                    <span className="text-sm text-muted-foreground">
                      {user.name?.split(' ')[0]}
                    </span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={onLogout}
                    className="text-muted-foreground hover:text-foreground"
                    data-testid="logout-btn"
                  >
                    <LogOut className="w-4 h-4" />
                  </Button>
                </>
              )}
            </div>

            {/* Mobile - Menu Button */}
            <div className="flex md:hidden items-center gap-2">
              {user?.picture && (
                <img 
                  src={user.picture} 
                  alt={user.name} 
                  className="w-8 h-8 rounded-full"
                />
              )}
              <button
                className="p-2 -mr-2 touch-target"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                data-testid="mobile-menu-btn"
                aria-label="Menu"
              >
                {mobileMenuOpen ? (
                  <X className="w-6 h-6" />
                ) : (
                  <Menu className="w-6 h-6" />
                )}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Header tip banner — only on home, silver bg, rotates each entry */}
      {isHome && <HeaderTipBanner />}

      {/* Banner sottotitolo REMOVED (27/05/2026): "Traccia le tue giornate..." now lives
          in the header row next to "Hello Revela". */}

      {/* Mobile Bottom Navigation */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-background border-t border-border safe-area-bottom z-50">
        <div className="flex justify-around items-center h-16">
          {MAIN_NAV_ITEMS.map(({ path, label, icon: Icon }) => (
            <NavLink
              key={path}
              to={path}
              className={({ isActive }) =>
                `flex flex-col items-center justify-center py-2 px-3 min-w-[60px] ${
                  isActive 
                    ? "text-foreground" 
                    : "text-muted-foreground"
                }`
              }
            >
              <Icon className="w-5 h-5 mb-1" strokeWidth={isActive => isActive ? 2 : 1.5} />
              <span className="text-[10px] font-medium">{label}</span>
            </NavLink>
          ))}
          <NavLink
            to="/impostazioni"
            className={({ isActive }) =>
              `flex flex-col items-center justify-center py-2 px-3 min-w-[60px] ${
                isActive 
                  ? "text-foreground" 
                  : "text-muted-foreground"
              }`
            }
          >
            <Settings className="w-5 h-5 mb-1" strokeWidth={1.5} />
            <span className="text-[10px] font-medium">Altro</span>
          </NavLink>
        </div>
      </nav>

      {/* Mobile Slide-out Menu */}
      {mobileMenuOpen && (
        <div 
          className="md:hidden fixed inset-0 z-40 bg-black/50"
          onClick={() => setMobileMenuOpen(false)}
        >
          <div 
            className="absolute right-0 top-0 bottom-0 w-72 bg-background shadow-xl animate-slide-in-right"
            onClick={e => e.stopPropagation()}
          >
            <div className="p-4 border-b border-border">
              <div className="flex items-center gap-3">
                {user?.picture && (
                  <img 
                    src={user.picture} 
                    alt={user.name} 
                    className="w-12 h-12 rounded-full"
                  />
                )}
                <div>
                  <p className="font-medium">{user?.name}</p>
                  <p className="text-sm text-muted-foreground">{user?.email}</p>
                </div>
              </div>
            </div>
            
            <div className="p-2">
              {MAIN_NAV_ITEMS.map(({ path, label, icon: Icon }) => (
                <NavLink
                  key={path}
                  to={path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-4 py-3 rounded-lg mb-1 ${
                      isActive 
                        ? "bg-muted font-medium" 
                        : "hover:bg-muted/50"
                    }`
                  }
                >
                  <Icon className="w-5 h-5" />
                  <span>{label}</span>
                </NavLink>
              ))}
              
              <hr className="my-2" />
              
              <NavLink
                to="/impostazioni"
                onClick={() => setMobileMenuOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 rounded-lg mb-1 ${
                    isActive 
                      ? "bg-muted font-medium" 
                      : "hover:bg-muted/50"
                  }`
                }
              >
                <Settings className="w-5 h-5" />
                <span>Impostazioni</span>
              </NavLink>
              
              <button
                onClick={() => {
                  setMobileMenuOpen(false);
                  onLogout();
                }}
                className="flex items-center gap-3 px-4 py-3 rounded-lg w-full text-left text-red-600 hover:bg-red-50"
              >
                <LogOut className="w-5 h-5" />
                <span>Esci</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Navigation;
